import { CaseStatus } from "@/store/baseClientApi";
import { DataObject, ThumbnailDataObject } from "@/typed/CaseStateData";
import { ModelType } from "@/typed/ModelTypes";

const DB_NAME = "casesCacheDB";
const TIMESTAMP_INDEX = "timestampIndex";
const DB_VERSION = 2;
const INVALIDATION_TIMEOUT_MS = 5 * 60 * 60 * 1000; // 5 hrs in milliseconds (Must be longer than upload time + file conversion step)

const CaseIdToDBId = (caseId: number | string) => `case-${caseId}`;

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      expectedStores.forEach((storeName) => {
        if (db.objectStoreNames.contains(storeName)) {
          db.deleteObjectStore(storeName);
        }

        const store = db.createObjectStore(storeName);
        store.createIndex(TIMESTAMP_INDEX, "timestamp", { unique: false });
      });
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

const getStoreItem = async <T extends { timestamp: number }>(key: string, store: IDBObjectStore) => {
  const request = store.get(key);

  return await new Promise<T | undefined>((resolve, reject) => {
    request.onsuccess = () => {
      const storeItem = request.result as T;
      if (storeItem) {
        storeItem.timestamp = Date.now();
        store.put(storeItem, key);

        resolve(storeItem);
      }
      resolve(undefined);
      return;
    };
    request.onerror = () => reject(request.error);
  });
};

export const transactionClosed = (tx: IDBTransaction) => {
  return new Promise<void>((resolve, reject) => {
    tx.onerror = function () {
      reject(this.error);
    };
    tx.onabort = function () {
      reject(this.error);
    };
    tx.oncomplete = function () {
      resolve();
    };
  });
};

export enum Store {
  ScanThumbnailStore = "scanThumbnails",
  OutputThumbnailStore = "outputThumbnails",
  ScanStore = "scans",
  ModelOutputStore = "outputs"
}

export function getStoreOfModel(modelType: ModelType): Store {
  switch (modelType) {
    case "scan":
      return Store.ScanStore;
    case "based-model":
      return Store.ModelOutputStore; // "based" is now "output"
    case "thumbnail":
      return Store.ScanThumbnailStore;
  }
}

export const expectedStores: Store[] = [Store.ScanThumbnailStore, Store.OutputThumbnailStore, Store.ScanStore, Store.ModelOutputStore];

// Define the structure so that "data" is a plain array.
export interface DataObjectIndexedDB {
  data: DataObject[]; // Now a plain array of DataObject.
  timestamp: number; // Used for cache invalidation.
}

export interface ThumbnailDataObjectIndexedDB {
  data: ThumbnailDataObject[];
  caseStatus: CaseStatus;
  timestamp: number; // Used for cache invalidation.
}

// Save case thumbnails to indexeddb
export const saveCaseThumbnailToIndexedDB = async (caseId: string, thumbnails: ThumbnailDataObjectIndexedDB, storeName: Store) => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  thumbnails.timestamp = Date.now();
  store.put(thumbnails, CaseIdToDBId(caseId));

  await transactionClosed(transaction);
};

// Update new thumbnail data by reading the existing record and adding/replacing new data into the array.
export const updateThumbnailDataInIndexedDB = async (
  caseId: string,
  caseStatus: CaseStatus,
  newData: ThumbnailDataObject[],
  storeName: Store
) => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);
  const key = CaseIdToDBId(caseId);

  const storeItem = await getStoreItem<ThumbnailDataObjectIndexedDB>(key, store);

  if (storeItem) {
    const updatedData = storeItem.data;
    newData.forEach((newThumbnail) => {
      const existingIndex = updatedData.findIndex((existingThumbnail) => existingThumbnail.label === newThumbnail.label);

      if (existingIndex !== -1) {
        // If a thumbnail with the same label exists, replace the existing one
        updatedData[existingIndex] = newThumbnail;
      } else {
        // Otherwise, append the new thumbnail
        updatedData.push(newThumbnail);
      }
    });

    // Update the store with the modified data
    storeItem.data = updatedData;
    storeItem.timestamp = Date.now();
    store.put(storeItem, key);
  } else {
    // If no data exists for this key, create a new item
    const newStoreItem: ThumbnailDataObjectIndexedDB = {
      data: newData,
      caseStatus,
      timestamp: Date.now()
    };

    store.put(newStoreItem, key);
  }

  await transactionClosed(transaction);
};

export const getCaseThumbnailsFromIndexedDB = async (caseId: number, caseStatus: CaseStatus, storeName: Store, invalidate?: boolean) => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);
  const key = CaseIdToDBId(caseId);

  const storeItem = await getStoreItem<ThumbnailDataObjectIndexedDB>(key, store);

  if (
    invalidate &&
    storeItem &&
    storeItem.caseStatus !== caseStatus &&
    (caseStatus === "files_converted" || caseStatus === "preprocessed")
  ) {
    store.delete(key);
    return undefined;
  }

  return storeItem?.data;
};

// Retrieve case thumbnail from indexeddb as a URL string
export const getCaseThumbnailFromIndexedDB = async (
  caseId: number,
  caseStatus: CaseStatus,
  thumbnailLabel: "combined" | "upper" | "lower" | "unspecified",
  storeName: Store,
  invalidate?: boolean
) => {
  const data = await getCaseThumbnailsFromIndexedDB(caseId, caseStatus, storeName, invalidate);
  if (data === undefined) {
    return;
  }

  const selectedThumbnail = data.find((thumbnail) => thumbnail.label === thumbnailLabel);
  if (selectedThumbnail && selectedThumbnail.data) {
    const thumbnailBlob = new Blob([selectedThumbnail.data]);
    return URL.createObjectURL(thumbnailBlob);
  }
};

// Save a new cache record with the standardized structure.
export const saveDataToIndexedDB = async (caseId: string, data: DataObject[], storeName: Store) => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  const storeItem: DataObjectIndexedDB = {
    data,
    timestamp: Date.now()
  };
  store.put(storeItem, CaseIdToDBId(caseId));

  await transactionClosed(transaction);
};

// Get the cached data record and return the full object (so that the caller can access ".data").
export const getDataFromIndexedDB = async (caseId: string, storeName: Store): Promise<DataObjectIndexedDB | undefined> => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  return await getStoreItem(CaseIdToDBId(caseId), store);
};

export const checkDataInIndexedDB = async (caseId: number, storeName: Store): Promise<boolean> => {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    const request = store.count(CaseIdToDBId(caseId));
    request.onsuccess = function () {
      resolve(request.result > 0);
    };
    request.onerror = function (event) {
      reject((event.target as IDBRequest).error);
    };
  });
};

/**
 * This will invalidate all data older than INDEXED_DB_INVALIDATION_TIMEOUT_MS.
 *
 * It is assumed that INDEXED_DB_INVALIDATION_TIMEOUT_MS is longer than it will take to upload and convert a case.
 * New assets can't be downloaded until a case has been converted. This assumption allows us to
 * invalidate cases stuck in uploading without having to worry about distinguishing between
 * stuck and uploading cases.
 */
export const invalidateIndexedDBData = async () => {
  const db = await openDatabase();

  for (const storeName of expectedStores) {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const index = store.index(TIMESTAMP_INDEX);

    const indexCursor = index.openKeyCursor(IDBKeyRange.upperBound(Date.now() - INVALIDATION_TIMEOUT_MS));

    await new Promise<void>((resolve, reject) => {
      indexCursor.onsuccess = function () {
        const cursor = this.result;
        if (cursor) {
          // Calling store.delete(cursor.primaryKey) is more efficient because cursor.delete() requires
          // using openCursor instead of openKeyCursor, and openCursor has to load the entire object from indexeddb.
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
        resolve();
      };
      indexCursor.onerror = function () {
        reject(this.error);
      };
    });
  }
};

export { INVALIDATION_TIMEOUT_MS as INDEXED_DB_INVALIDATION_TIMEOUT_MS };
